import { useState, useEffect, useCallback } from 'react';
import {
  adminGetUsuarios, adminGetEmpresas,
  adminCriarUsuario, adminEditarUsuario,
  adminDeletarUsuario, adminToggleUsuario,
  adminResetarSenha,
} from '../services/api';
import { PageHeader, SearchBar, Loading, Erro, Badge } from '../components/UI';
import './Empresas.css';
import './Usuarios.css';

const VAZIO_FORM  = { nome: '', email: '', senha: '', empresa_ids: [], is_admin: false };
const VAZIO_SENHA = { nova_senha: '', confirmar: '' };

// ─── Chips de empresa ─────────────────────────────────────────────────────────

function EmpresaChips({ empresas = [] }) {
  if (!empresas.length) return <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>—</span>;
  return (
    <div className="usu-chips">
      {empresas.map(e => (
        <span key={e.id} className="usu-chip">{e.nome}</span>
      ))}
    </div>
  );
}

// ─── Multi-select de empresas ─────────────────────────────────────────────────

function EmpresaMultiSelect({ empresas, selected, onChange }) {
  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter(x => x !== id));
    } else {
      onChange([...selected, id]);
    }
  };
  return (
    <div className="usu-multi">
      {empresas.map(e => (
        <label key={e.id} className={`usu-multi-item${selected.includes(e.id) ? ' usu-multi-item--sel' : ''}`}>
          <input
            type="checkbox"
            checked={selected.includes(e.id)}
            onChange={() => toggle(e.id)}
            style={{ display: 'none' }}
          />
          <span className="usu-multi-check">{selected.includes(e.id) ? '✓' : ''}</span>
          <span className="usu-multi-nome">{e.nome}</span>
        </label>
      ))}
      {empresas.length === 0 && (
        <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>Nenhuma empresa cadastrada.</span>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [busca,    setBusca]    = useState('');
  const [load,     setLoad]     = useState(true);
  const [erro,     setErro]     = useState('');

  const [modal,    setModal]    = useState(false);
  const [editando, setEditando] = useState(null);
  const [form,     setForm]     = useState(VAZIO_FORM);
  const [formErro, setFormErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [modalSenha,    setModalSenha]    = useState(null);
  const [senhaForm,     setSenhaForm]     = useState(VAZIO_SENHA);
  const [senhaErro,     setSenhaErro]     = useState('');
  const [salvandoSenha, setSalvandoSenha] = useState(false);

  const [confirmDel, setConfirmDel] = useState(null);
  const [deletando,  setDeletando]  = useState(false);

  const carregar = useCallback(async () => {
    setLoad(true); setErro('');
    try {
      const [u, e] = await Promise.all([adminGetUsuarios(), adminGetEmpresas()]);
      setUsuarios(u);
      setEmpresas(e);
    } catch (e) { setErro(e.message); }
    finally { setLoad(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const filtrados = usuarios.filter(u =>
    u.nome.toLowerCase().includes(busca.toLowerCase()) ||
    u.email.toLowerCase().includes(busca.toLowerCase()) ||
    (u.empresas || []).some(e => e.nome.toLowerCase().includes(busca.toLowerCase()))
  );

  // ─── Modais ─────────────────────────────────────────────────────────────────

  const abrirNovo = () => {
    setEditando(null);
    setForm(VAZIO_FORM);
    setFormErro('');
    setModal(true);
  };

  const abrirEditar = (u) => {
    setEditando(u);
    setForm({
      nome:        u.nome,
      email:       u.email,
      senha:       '',
      empresa_ids: (u.empresas || []).map(e => e.id),
      is_admin:    !!u.is_admin,
    });
    setFormErro('');
    setModal(true);
  };

  const fecharModal = () => { if (!salvando) { setModal(false); setEditando(null); } };

  const abrirSenha = (u) => {
    setModalSenha(u);
    setSenhaForm(VAZIO_SENHA);
    setSenhaErro('');
  };

  // ─── Salvar ──────────────────────────────────────────────────────────────────

  const salvar = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim()) {
      setFormErro('Preencha nome e e-mail.'); return;
    }
    if (!form.empresa_ids.length) {
      setFormErro('Selecione ao menos uma empresa.'); return;
    }
    if (!editando && !form.senha.trim()) {
      setFormErro('A senha é obrigatória para novos usuários.'); return;
    }
    setSalvando(true); setFormErro('');
    try {
      const dados = {
        nome:        form.nome.trim(),
        email:       form.email.trim(),
        empresa_ids: form.empresa_ids,
        is_admin:    form.is_admin,
      };
      if (editando) {
        await adminEditarUsuario(editando.id, dados);
      } else {
        await adminCriarUsuario({ ...dados, senha: form.senha });
      }
      setModal(false);
      await carregar();
    } catch (err) {
      setFormErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  // ─── Alterar senha ───────────────────────────────────────────────────────────

  const salvarSenha = async (e) => {
    e.preventDefault();
    if (!senhaForm.nova_senha.trim()) { setSenhaErro('Digite a nova senha.'); return; }
    if (senhaForm.nova_senha !== senhaForm.confirmar) { setSenhaErro('As senhas não coincidem.'); return; }
    if (senhaForm.nova_senha.length < 6) { setSenhaErro('A senha deve ter no mínimo 6 caracteres.'); return; }
    setSalvandoSenha(true); setSenhaErro('');
    try {
      await adminResetarSenha(modalSenha.id, senhaForm.nova_senha);
      setModalSenha(null);
    } catch (err) {
      setSenhaErro(err.message);
    } finally {
      setSalvandoSenha(false);
    }
  };

  // ─── Toggle / Delete ──────────────────────────────────────────────────────────

  const toggle = async (u) => {
    try {
      const res = await adminToggleUsuario(u.id);
      setUsuarios(prev => prev.map(x => x.id === u.id ? { ...x, ativo: res.ativo } : x));
    } catch (err) { alert(err.message); }
  };

  const deletar = async () => {
    if (!confirmDel) return;
    setDeletando(true);
    try {
      await adminDeletarUsuario(confirmDel.id);
      setConfirmDel(null);
      await carregar();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeletando(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader titulo="Usuários" subtitulo="Gerencie os usuários e seus acessos">
        <button className="emp-novo-btn" onClick={abrirNovo}>+ Novo Usuário</button>
      </PageHeader>

      <div className="emp-toolbar">
        <SearchBar value={busca} onChange={setBusca} placeholder="Buscar por nome, e-mail ou empresa..." />
        <span className="emp-total">{filtrados.length} usuário{filtrados.length !== 1 ? 's' : ''}</span>
      </div>

      {erro && <Erro mensagem={erro} onRetry={carregar} />}
      {load ? <Loading /> : (
        <div className="usu-lista">
          {filtrados.length === 0 && (
            <div className="tbl-vazio">Nenhum usuário encontrado.</div>
          )}
          {filtrados.map(u => (
            <div key={u.id} className="usu-card">
              <div className="usu-card-main">
                <div className="usu-card-avatar">{u.nome?.[0]?.toUpperCase() || 'U'}</div>
                <div className="usu-card-info">
                  <div className="usu-card-nome">
                    {u.nome}
                    {!!u.is_admin && <Badge valor="Admin" tipo="warning" />}
                    <Badge valor={u.ativo ? 'Ativo' : 'Inativo'} tipo={u.ativo ? 'success' : 'danger'} />
                  </div>
                  <div className="usu-card-email">{u.email}</div>
                  <EmpresaChips empresas={u.empresas} />
                </div>
              </div>
              <div className="emp-acoes">
                <button className="emp-btn emp-btn--edit" onClick={() => abrirEditar(u)} title="Editar"><IcoEdit /></button>
                <button className="emp-btn usu-btn--key" onClick={() => abrirSenha(u)} title="Alterar senha"><IcoKey /></button>
                <button
                  className={`emp-btn ${u.ativo ? 'emp-btn--off' : 'emp-btn--on'}`}
                  onClick={() => toggle(u)}
                  title={u.ativo ? 'Desativar' : 'Ativar'}
                >
                  {u.ativo ? <IcoOff /> : <IcoOn />}
                </button>
                <button className="emp-btn emp-btn--del" onClick={() => setConfirmDel(u)} title="Excluir"><IcoTrash /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── Modal Form ───────────────────────────────────────────────────── */}
      {modal && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editando ? 'Editar Usuário' : 'Novo Usuário'}</h2>
              <button className="modal-close" onClick={fecharModal}>✕</button>
            </div>
            <form onSubmit={salvar} className="modal-form">
              <div className="mf-group">
                <label>Nome *</label>
                <input
                  value={form.nome}
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                  placeholder="Nome completo"
                  required
                />
              </div>
              <div className="mf-row">
                <div className="mf-group">
                  <label>E-mail *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="email@empresa.com"
                    required
                  />
                </div>
                {!editando && (
                  <div className="mf-group">
                    <label>Senha *</label>
                    <input
                      type="password"
                      value={form.senha}
                      onChange={e => setForm(f => ({ ...f, senha: e.target.value }))}
                      placeholder="Mínimo 6 caracteres"
                    />
                  </div>
                )}
              </div>

              <div className="mf-group">
                <label>Empresas * <span className="mf-label-hint">(selecione uma ou mais)</span></label>
                <EmpresaMultiSelect
                  empresas={empresas.filter(e => e.ativo)}
                  selected={form.empresa_ids}
                  onChange={ids => setForm(f => ({ ...f, empresa_ids: ids }))}
                />
              </div>

              <label className="mf-check">
                <input
                  type="checkbox"
                  checked={form.is_admin}
                  onChange={e => setForm(f => ({ ...f, is_admin: e.target.checked }))}
                />
                <span>Administrador (acesso ao painel de gestão)</span>
              </label>

              {formErro && <div className="mf-erro">⚠ {formErro}</div>}
              <div className="modal-footer">
                <button type="button" className="mf-btn mf-btn--cancel" onClick={fecharModal} disabled={salvando}>Cancelar</button>
                <button type="submit" className="mf-btn mf-btn--save" disabled={salvando}>
                  {salvando ? 'Salvando...' : (editando ? 'Salvar alterações' : 'Cadastrar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Modal Alterar Senha ──────────────────────────────────────────── */}
      {modalSenha && (
        <div className="modal-overlay" onClick={() => !salvandoSenha && setModalSenha(null)}>
          <div className="modal-box modal-box--sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Alterar Senha</h2>
              <button className="modal-close" onClick={() => setModalSenha(null)}>✕</button>
            </div>
            <form onSubmit={salvarSenha} className="modal-form">
              <p className="usu-senha-user">Usuário: <strong>{modalSenha.nome}</strong></p>
              <div className="mf-group">
                <label>Nova senha *</label>
                <input type="password" value={senhaForm.nova_senha} onChange={e => setSenhaForm(f => ({ ...f, nova_senha: e.target.value }))} placeholder="Mínimo 6 caracteres" required />
              </div>
              <div className="mf-group">
                <label>Confirmar senha *</label>
                <input type="password" value={senhaForm.confirmar} onChange={e => setSenhaForm(f => ({ ...f, confirmar: e.target.value }))} placeholder="Repita a nova senha" required />
              </div>
              {senhaErro && <div className="mf-erro">⚠ {senhaErro}</div>}
              <div className="modal-footer">
                <button type="button" className="mf-btn mf-btn--cancel" onClick={() => setModalSenha(null)} disabled={salvandoSenha}>Cancelar</button>
                <button type="submit" className="mf-btn mf-btn--save" disabled={salvandoSenha}>
                  {salvandoSenha ? 'Salvando...' : 'Alterar senha'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Modal Confirmar Delete ────────────────────────────────────────── */}
      {confirmDel && (
        <div className="modal-overlay" onClick={() => !deletando && setConfirmDel(null)}>
          <div className="modal-box modal-box--sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar exclusão</h2>
              <button className="modal-close" onClick={() => setConfirmDel(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p>Tem certeza que deseja excluir o usuário <strong>{confirmDel.nome}</strong>?</p>
              <p className="modal-warn">Esta ação não pode ser desfeita.</p>
            </div>
            <div className="modal-footer">
              <button className="mf-btn mf-btn--cancel" onClick={() => setConfirmDel(null)} disabled={deletando}>Cancelar</button>
              <button className="mf-btn mf-btn--danger" onClick={deletar} disabled={deletando}>
                {deletando ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IcoEdit()  { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function IcoTrash() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>; }
function IcoOff()   { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>; }
function IcoOn()    { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>; }
function IcoKey()   { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>; }
